import React, { Component } from 'react';
import multiKeyNameConflict from 'app/forms/config/validators/multiKeyNameConflict';
import { inject, observer } from 'mobx-react';
import { Field, FormDialog, InputGroup, InputMultiple, PasswordInput, SingleLineErrors } from 'core/form';
import { Box, Button, Card, Flex, Text } from 'core/components';

const fields = {
  name: {
    label: 'Name',
    rules: 'required|alpha_dash',
    transform: {
      out: (value) => value.trim()
    },
    messages: {
      required: 'MCP name is required',
      not_in: 'Name must be unique'
    }
  },
  description: {
    label: 'Description'
  },
  type: {
    defaultValue: 'mcp_http_headers'
  },
  tokens: {
    isComplexArray: true
  },
  'tokens[].key': {
    label: 'Header Name',
    rules: `required|alpha_dash|${multiKeyNameConflict}`,
    placeholder: 'e.g. Authorization, X-API-Key',
    messages: {
      required: 'Header name is required'
    }
  },
  'tokens[].value': {
    label: 'Header Value',
    rules: 'required',
    placeholder: 'e.g. Bearer ...',
    messages: {
      required: 'Header value is required'
    }
  },
  labels: {
    label: 'Labels',
    defaultValue: []
  }
};

@inject('$credentials')
@observer
class CreateMcpCredentialDialog extends Component {
  state = {
    isLoading: false
  };

  form;

  componentDidMount() {
    const tokensField = this.form.getField('tokens');
    tokensField.setValue([]);
    tokensField.add({ key: '', value: '' });
  }

  handleSave = async (form, values) => {
    const { $credentials, model, onClose, onSave } = this.props;
    this.setState({ isLoading: true });

    try {
      await model.save(values);
      await $credentials.groupedCollection.fetch({ force: true });

      if (onSave) {
        onSave(values.name);
      }

      onClose();
    } finally {
      this.setState({ isLoading: false });
    }
  };

  render() {
    const { $credentials, model, onClose } = this.props;
    const { isLoading } = this.state;

    const nameRules = `${fields.name.rules}|not_in:${$credentials.groupedCollection.get().map((cred) => cred.get('name'))}`;

    return (
      <FormDialog
        entityName="MCP Credential"
        fields={fields}
        options={{ name: 'MCP Credentials Dialog Form', showPristineErrors: false }}
        model={model}
        onClose={onClose}
        width={700}
        formComponentProps={{
          onLoad: (form) => (this.form = form)
        }}
        formActionsProps={{
          onSubmit: this.handleSave,
          onCancel: onClose,
          forceLoading: isLoading
        }}
        isOpen
      >
        <Field name="name" autoFocus fill large rules={nameRules}>
          <InputGroup />
        </Field>
        <Field name="description" fill large>
          <InputGroup />
        </Field>
        <Card p={2}>
          <Box mb={2}>
            <Text fontWeight={600}>HTTP Headers</Text>
          </Box>
          <Field name="tokens">
            {({ field: tokensField }) => (
              <Flex flexDirection="column" gap="12px">
                <InputMultiple field={tokensField}>
                  {({ field }) => (
                    <>
                      {field.map((f, index) => (
                        <React.Fragment key={f.key._id}>
                          <Flex alignItems="center" gap="12px">
                            <Field field={f.key} showError={false} showLabel={index === 0} large width={200} m={0}>
                              <InputGroup />
                            </Field>
                            <Field field={f.value} large flex={1} m={0} showError={false} showLabel={index === 0} fill>
                              <PasswordInput showVisibleToggle={f.value.dirty} autoComplete="new-password" />
                            </Field>
                            {field.size() > 1 && (
                              <Button
                                icon="trash"
                                minimal
                                small
                                onClick={() => tokensField.remove(index)}
                                mt={index === 0 ? '20px' : 0}
                              />
                            )}
                          </Flex>
                          <SingleLineErrors fields={[f.key, f.value]} inline />
                        </React.Fragment>
                      ))}
                      <Button
                        icon="plus"
                        small
                        text="Add Header"
                        onClick={() => tokensField.add({ key: '', value: '' })}
                      />
                    </>
                  )}
                </InputMultiple>
              </Flex>
            )}
          </Field>
        </Card>
      </FormDialog>
    );
  }
}

export default CreateMcpCredentialDialog;
